import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResourceCrudPage,
  Stack,
  TabView,
  type ResourceListArgs,
  type PaginatedResult,
} from '@vipso/shared';
import {
  useListCategoriesQuery,
  useAddUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useListUnitsQuery,
  useAddUpdateUnitMutation,
  useDeleteUnitMutation,
} from '../../api/tenantApi';
import { NoAccessState } from '../../components/RequireFeature';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { useNavAccess } from '../../hooks/useNavAccess';

type NamedRow = { id: string; name: string };

function useCategoryList(args: ResourceListArgs) {
  const result = useListCategoriesQuery(args);
  const data: PaginatedResult<NamedRow> | undefined = result.data?.listCategories
    ? {
        ...result.data.listCategories,
        rows: result.data.listCategories.rows.map((r) => ({ id: r.id, name: r.name })),
      }
    : undefined;
  return { ...result, data };
}

function CategoriesTab() {
  const { t } = useTranslation();
  const access = useModuleAccess('category');
  const [save, { isLoading: saving }] = useAddUpdateCategoryMutation();
  const [remove] = useDeleteCategoryMutation();

  return (
    <ResourceCrudPage<NamedRow>
      title={t('settings.categories')}
      description={t('settings.categoriesDescription')}
      entityLabel={t('settings.categoryEntity')}
      entityLabelPlural={t('settings.categories')}
      canCreate={access.canCreate}
      canUpdate={access.canUpdate}
      canDelete={access.canDelete}
      saving={saving}
      useList={useCategoryList}
      fields={[{ key: 'name', label: t('common.name'), required: true }]}
      onSave={(values, editing) => save({ ...values, id: editing?.id }).unwrap()}
      onDelete={(row) => remove({ id: row.id }).unwrap()}
    />
  );
}

function useUnitList(args: ResourceListArgs) {
  const result = useListUnitsQuery(args);
  const data: PaginatedResult<NamedRow> | undefined = result.data?.listUnits
    ? {
        ...result.data.listUnits,
        rows: result.data.listUnits.rows.map((r) => ({ id: r.id, name: r.name })),
      }
    : undefined;
  return { ...result, data };
}

function UnitsTab() {
  const { t } = useTranslation();
  const access = useModuleAccess('units');
  const [save, { isLoading: saving }] = useAddUpdateUnitMutation();
  const [remove] = useDeleteUnitMutation();

  return (
    <ResourceCrudPage<NamedRow>
      title={t('settings.units')}
      description={t('settings.unitsDescription')}
      entityLabel={t('settings.unitEntity')}
      entityLabelPlural={t('settings.units')}
      canCreate={access.canCreate}
      canUpdate={access.canUpdate}
      canDelete={access.canDelete}
      saving={saving}
      useList={useUnitList}
      fields={[{ key: 'name', label: t('common.name'), required: true }]}
      onSave={(values, editing) => save({ ...values, id: editing?.id }).unwrap()}
      onDelete={(row) => remove({ id: row.id }).unwrap()}
    />
  );
}

/** Category + unit CRUD for Settings (same BottomDrawer pattern as storage). */
export function CatalogManagement() {
  const { t } = useTranslation();
  const nav = useNavAccess();

  const allowedTabs = useMemo(() => {
    const tabs: Array<{ value: string; title: string; content: React.ReactNode }> = [];
    if (nav.categories) {
      tabs.push({
        value: 'categories',
        title: t('settings.categories'),
        content: <CategoriesTab />,
      });
    }
    if (nav.units) {
      tabs.push({
        value: 'units',
        title: t('settings.units'),
        content: <UnitsTab />,
      });
    }
    return tabs;
  }, [nav.categories, nav.units, t]);

  const [tab, setTab] = useState(() => allowedTabs[0]?.value ?? 'categories');

  useEffect(() => {
    if (!allowedTabs.length) return;
    if (!allowedTabs.some((item) => item.value === tab)) {
      setTab(allowedTabs[0].value);
    }
  }, [allowedTabs, tab]);

  if (!allowedTabs.length) {
    return <NoAccessState />;
  }

  return (
    <Stack gap="md">
      <TabView value={tab} onChange={setTab} tabs={allowedTabs} />
    </Stack>
  );
}
